import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapView } from '@/components/MapView';
import { Logo } from '@/components/Logo';
import { api } from '@/lib/api';
import { getRoute, remainingMinutes } from '@/lib/route';
import { useT } from '@/lib/i18n';
import { timeAgo, VEHICLE_LABELS, fmtDate } from '@/lib/format';
import { CheckCircle2, ShieldCheck, Loader2, AlertCircle, PauseCircle } from 'lucide-react';

export default function PublicShare() {
  const { token } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [routeCoords, setRouteCoords] = useState([]);
  const [routeInfo, setRouteInfo] = useState(null);

  const load = useCallback(async () => {
    try { const d = await api.publicRide(token); setData(d); setErr(false); } catch { setErr(true); }
  }, [token]);

  useEffect(() => { load(); api.publicOpened(token).catch(() => {}); api.track('shared_link_opened'); }, [load, token]);
  useEffect(() => { const tm = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(tm); }, []);
  useEffect(() => {
    if (data?.status === 'completed') return;
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load, data?.status]);

  useEffect(() => {
    // Compute the planned route once. The routeCoords.length guard makes this
    // safe to depend on `data` directly (re-runs on each poll but early-returns
    // once a route exists), so we can list honest deps with no eslint-disable.
    if (!data || data.destination_lat == null || routeCoords.length) return;
    const originPt = data.origin_lat != null ? { latitude: data.origin_lat, longitude: data.origin_lng }
      : (data.latest_location ? { latitude: data.latest_location.latitude, longitude: data.latest_location.longitude } : null);
    const destPt = { latitude: data.destination_lat, longitude: data.destination_lng };
    if (!originPt) return;
    getRoute(originPt, destPt).then((r) => {
      if (r && r.coords) { setRouteCoords(r.coords); setRouteInfo(r); }
      else { setRouteCoords([originPt, destPt]); setRouteInfo(null); }
    }).catch((e) => { console.debug('route fetch failed; showing straight line:', e); setRouteCoords([originPt, destPt]); });
  }, [data, routeCoords.length]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--background)' }}>
        <div className="text-center">
          <AlertCircle size={40} style={{ color: 'var(--text-muted)' }} className="mx-auto mb-3" />
          <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>{t('ps_unavailable')}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{t('ps_unavailable_b')}</p>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}><Loader2 className="animate-spin" style={{ color: 'var(--primary)' }} /></div>;

  const done = data.status === 'completed';
  const pos = data.latest_location;
  const etaMin = remainingMinutes(routeInfo, pos);
  const etaText = etaMin == null ? null : (etaMin <= 1 ? t('eta_soon') : `${t('eta_about')} ${etaMin} ${t('eta_min')}`);

  // Live-vs-stale badge: how fresh is the rider's last GPS point?
  const locAgeSec = pos?.recorded_at ? Math.max(0, Math.floor((now - new Date(pos.recorded_at).getTime()) / 1000)) : null;
  const liveState = locAgeSec == null ? 'waiting' : (locAgeSec <= 45 ? 'live' : (locAgeSec <= 300 ? 'stale' : 'lost'));
  const liveColor = { live: 'var(--success)', stale: 'var(--warning)', lost: 'var(--danger)', waiting: 'var(--text-muted)' }[liveState];
  const liveLabel = liveState === 'live' ? t('ps_badge_live')
    : liveState === 'waiting' ? t('ps_waiting')
      : `${t('ps_last_seen')} ${timeAgo(pos.recorded_at, now)}`;

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <div className="mx-auto max-w-md px-4 pt-4 pb-8">
        <div className="flex items-center justify-between mb-3"><Logo /><span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('ps_live')}</span></div>

        {done ? (
          <div className="rounded-2xl p-5 border text-center mb-3" style={{ background: 'var(--surface)', borderColor: 'var(--success)', boxShadow: 'var(--shadow)' }} data-testid="public-ride-status">
            <CheckCircle2 size={36} style={{ color: 'var(--success)' }} className="mx-auto mb-2" />
            <h1 className="text-2xl font-extrabold" style={{ color: 'var(--text)' }}>{t('ps_arrived')}</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{data.passenger_name} {t('ps_arrived_at')} {fmtDate(data.ended_at)}</p>
          </div>
        ) : (
          <div className="rounded-2xl p-4 border mb-3" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="public-ride-status">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full ss-pulse" style={{ background: 'var(--primary)' }} />
                <span className="font-bold" style={{ color: 'var(--primary)' }}>{t('ps_active')}</span>
              </div>
              <div
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
                style={{ background: `color-mix(in srgb, ${liveColor} 14%, transparent)` }}
                data-testid="public-live-badge"
              >
                {liveState === 'live'
                  ? <span className="w-2 h-2 rounded-full ss-pulse" style={{ background: liveColor }} />
                  : <PauseCircle size={13} style={{ color: liveColor }} />}
                <span className="text-xs font-bold" style={{ color: liveColor }} data-testid="public-last-updated">{liveLabel}</span>
              </div>
            </div>
            {(liveState === 'stale' || liveState === 'lost') && (
              <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }} data-testid="public-paused-note">{t('ps_paused_note')}</p>
            )}
          </div>
        )}

        {!done && etaText && (
          <div className="rounded-2xl px-3 py-2 mb-3 text-sm font-semibold text-center" style={{ background: 'var(--surface-alt)', color: 'var(--primary)' }} data-testid="public-ride-eta">
            🕒 {etaText}
          </div>
        )}

        <div style={{ height: '42vh' }} className="rounded-2xl overflow-hidden border mb-3" data-testid="public-ride-map">
          <MapView position={pos} route={routeCoords} path={data.recent_path || []}
            origin={data.origin_lat != null ? { latitude: data.origin_lat, longitude: data.origin_lng } : null}
            destination={data.destination_lat != null ? { latitude: data.destination_lat, longitude: data.destination_lng } : null}
            interactive={true} testid="public-map" />
        </div>

        <div className="rounded-2xl p-4 border space-y-3" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3">
            {data.photos?.[0] ? <img src={data.photos[0].data_url} alt="Vehicle" className="w-14 h-14 rounded-xl object-cover border" style={{ borderColor: 'var(--border)' }} /> : <span className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: 'var(--surface-alt)' }}><ShieldCheck style={{ color: 'var(--primary)' }} /></span>}
            <div>
              <div className="font-bold" style={{ color: 'var(--text)' }}>{data.passenger_name}</div>
              <div className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>{data.public_ride_id}</div>
            </div>
          </div>
          <Row label={t('ps_vehicle')} value={VEHICLE_LABELS[data.vehicle_type] || data.vehicle_type} />
          <Row label={t('ps_goingto')} value={data.destination_text || t('not_set')} />
          <Row label={t('ps_started')} value={fmtDate(data.started_at)} />
        </div>

        {done && (
          <button onClick={() => navigate('/')} className="mt-4 w-full rounded-full font-bold min-h-[52px]" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="public-get-app-button">{t('ps_getapp')}</button>
        )}
        <p className="text-xs text-center mt-4" style={{ color: 'var(--text-muted)' }}>{t('disclaimer')}</p>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span style={{ color: 'var(--text-muted)' }}>{label}</span>
    <span className="font-semibold text-right" style={{ color: 'var(--text)' }}>{value}</span>
  </div>
);
